import React, { useEffect, useState } from "react";
import axios from "axios";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { MessageSquare, Clock, Image, Zap } from "lucide-react";
import { API } from "../../App";
import TopUpDialog from "../usage/TopUpDialog";
import { isIOSApp } from "../../hooks/useIsMobileApp";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmt = (n) => (n === null || n === undefined || n === -1 ? "∞" : Number(n).toLocaleString());
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

const barColor = (pct) => {
  if (pct === null) return "bg-emerald-500";
  if (pct >= 100) return "bg-red-500";
  if (pct >= 90) return "bg-orange-500";
  if (pct >= 75) return "bg-amber-500";
  return "bg-blue-500";
};

const UsageBar = ({ icon: Icon, iconColor, label, block, onBuy, testId }) => {
  const pct = block.unlimited ? null : Math.min(block.pct || 0, 100);
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between flex-wrap gap-1">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm text-muted-foreground">{label}</span>
          {block.topup > 0 && <span className="text-[10px] text-emerald-500">+{fmt(block.topup)} top-up</span>}
        </div>
        <span className="text-sm font-semibold text-foreground tabular-nums">
          {fmt(block.used)} / {block.unlimited ? "∞" : fmt(block.allowance)}
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden bg-muted/60">
        <div className={`h-full ${barColor(pct)} transition-all`}
          style={{ width: `${block.unlimited ? 4 : pct}%` }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">
          {!block.unlimited && <>{(block.pct || 0).toFixed(1)}% used · <span className="text-foreground">{fmt(block.remaining)} remaining</span></>}
        </span>
        {!block.unlimited && (block.pct || 0) >= 75 && !isIOSApp() && (
          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={onBuy}
            data-testid={`${testId}-buy-btn`}>
            <Zap className="w-3 h-3 mr-1 text-[#FFCC00]" /> Buy More
          </Button>
        )}
      </div>
    </div>
  );
};

export const StoreUsageCard = () => {
  const [usage, setUsage] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyType, setBuyType] = useState(null);

  useEffect(() => {
    axios.get(`${API}/usage/account`, getAuthConfig())
      .then(res => setUsage(res.data))
      .catch(() => {});
  }, []);

  if (!usage) return null;
  const openBuy = (t) => { setBuyType(t); setBuyOpen(true); };
  const hasLocs = usage.sms.by_location?.length > 0 || usage.minutes.by_location?.length > 0;

  return (
    <div className="rounded-xl border border-border bg-card/60 backdrop-blur-sm p-4" data-testid="store-usage-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
        <h2 className="text-base font-semibold text-foreground font-['Manrope'] flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-blue-500" /> Messages & Minutes Usage
        </h2>
        <Badge className="bg-blue-500/15 text-blue-500 border-0">{usage.plan?.name}</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3" data-testid="store-usage-period">
        Billing period {fmtDate(usage.period?.start)} – {fmtDate(usage.period?.end)} · Next reset: {fmtDate(usage.period?.next_reset)}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <UsageBar
          icon={MessageSquare} iconColor="text-purple-500" label="Messages"
          block={usage.sms} onBuy={() => openBuy("sms")} testId="usage-sms-bar"
        />
        <UsageBar
          icon={Clock} iconColor="text-orange-500" label="Minutes"
          block={usage.minutes} onBuy={() => openBuy("minutes")} testId="usage-minutes-bar"
        />
      </div>

      {hasLocs && (
        <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1"
          data-testid="store-usage-locations">
          {usage.sms.by_location?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">SMS by location</p>
              {usage.sms.by_location.map((l, i) => (
                <div key={i} className="flex justify-between text-xs py-0.5" data-testid={`usage-loc-sms-${l.location_id || 'unassigned'}`}>
                  <span className="text-muted-foreground">{l.name}</span>
                  <span className="text-foreground tabular-nums">{fmt(l.used)}</span>
                </div>
              ))}
            </div>
          )}
          {usage.minutes.by_location?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Minutes by location</p>
              {usage.minutes.by_location.map((l, i) => (
                <div key={i} className="flex justify-between text-xs py-0.5" data-testid={`usage-loc-min-${l.location_id || 'unassigned'}`}>
                  <span className="text-muted-foreground">{l.name}</span>
                  <span className="text-foreground tabular-nums">{fmt(l.used)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {usage.sms.breakdown?.mms > 0 && (
        <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1" data-testid="usage-media-note">
          <Image className="w-3 h-3" /> {usage.sms.breakdown.mms} media message{usage.sms.breakdown.mms !== 1 ? "s" : ""} this period — media counts ×{usage.mms_weight} toward usage
        </p>
      )}
      <TopUpDialog open={buyOpen} onOpenChange={setBuyOpen} usageType={buyType} />
    </div>
  );
};

export default StoreUsageCard;
